import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  BookOpen, Briefcase, Cog, Scale, Download, Eye, X,
  Calculator,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const CATEGORY_ICONS = {
  manuels: BookOpen,
  commerciaux: Briefcase,
  processus: Cog,
  juridique: Scale,
};

const FORMATS = [
  { id: "pdf", label: "PDF" },
  { id: "docx", label: "Word" },
  { id: "html", label: "HTML" },
  { id: "md", label: "Markdown" },
];

export default function AdminDocs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [showRoi, setShowRoi] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/docs/list");
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Chargement impossible");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openDoc = async (doc) => {
    try {
      const r = await api.get(`/docs/read?slug=${encodeURIComponent(doc.slug)}`);
      setViewing({ ...doc, ...r.data });
    } catch (e) {
      toast.error("Contenu indisponible");
    }
  };

  const exportDoc = async (slug, fmt) => {
    try {
      const r = await api.get(`/docs/export?slug=${encodeURIComponent(slug)}&fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const base = slug.split("/").pop();
      a.download = `${base}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} téléchargé`);
    } catch {
      toast.error(`Export ${fmt.toUpperCase()} échoué`);
    }
  };

  const totalDocs = useMemo(
    () => (data?.categories || []).reduce((n, c) => n + c.count, 0),
    [data],
  );

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="admin-docs-page">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-start justify-between mb-10 gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Console Fondateur</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">Documentation</h1>
            <p className="mt-5 text-base md:text-lg text-[#374151] max-w-2xl leading-relaxed">
              Bibliothèque complète des manuels, documents commerciaux, processus internes et modèles juridiques.
              Exportable en PDF · Word · HTML · Markdown.
            </p>
          </div>
          <button
            data-testid="docs-roi-btn"
            onClick={() => setShowRoi(true)}
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-700 text-white px-5 py-3 text-sm font-medium rounded-md shadow-sm transition-colors"
          >
            <Calculator className="h-4 w-4" strokeWidth={2} /> ROI Calculator
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-[#5B5B62]">Chargement…</div>
        ) : (
          <>
            <div className="text-[11px] font-mono text-[#8B8B8F] mb-4">{totalDocs} documents disponibles</div>
            <div className="space-y-6">
              {(data?.categories || []).map((cat) => {
                const Icon = CATEGORY_ICONS[cat.key] || BookOpen;
                return (
                  <section key={cat.key} data-testid={`docs-category-${cat.key}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
                      <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-[#5B5B62]">
                        {cat.label_fr} <span className="text-[#8B8B8F]">({cat.count})</span>
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cat.documents.map((doc) => (
                        <div key={doc.slug} className="border border-[#E4E1D8] bg-white rounded-md p-3 hover:shadow-sm transition-shadow flex items-center justify-between gap-3" data-testid={`docs-item-${doc.slug.replace('/', '-')}`}>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#1F2937] truncate">{doc.title}</div>
                            <div className="text-[10px] font-mono text-[#8B8B8F]">
                              {doc.filename} · {(doc.size_bytes / 1024).toFixed(1)} KB
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              data-testid={`docs-view-${doc.slug.replace('/', '-')}`}
                              onClick={() => openDoc(doc)}
                              className="p-1.5 border border-[#E4E1D8] rounded-md text-[#0B2545] hover:bg-[#F1EFE9]"
                              title="Prévisualiser"
                            >
                              <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                            <ExportMenu slug={doc.slug} onExport={exportDoc} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        )}
      </div>

      {viewing && <DocViewer doc={viewing} onClose={() => setViewing(null)} onExport={exportDoc} />}
      {showRoi && <RoiCalculator onClose={() => setShowRoi(false)} />}
    </div>
  );
}

function ExportMenu({ slug, onExport }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid={`docs-export-${slug.replace('/', '-')}`}
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 border border-[#E4E1D8] rounded-md text-[#0B2545] hover:bg-[#F1EFE9]"
        title="Exporter"
      >
        <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-[#E4E1D8] rounded-md shadow-lg z-30 min-w-[120px]" onMouseLeave={() => setOpen(false)}>
          {FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => { setOpen(false); onExport(slug, f.id); }}
              className="w-full text-left px-3 py-1.5 hover:bg-[#F1EFE9] text-xs first:rounded-t-md last:rounded-b-md"
            >
              {f.label} <span className="text-[10px] font-mono text-[#8B8B8F]">.{f.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DocViewer({ doc, onClose, onExport }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="docs-viewer-modal">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E1D8]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#5B5B62]">{doc.category}</div>
            <h3 className="text-lg font-display text-[#0B2545]">{doc.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                onClick={() => onExport(doc.slug, f.id)}
                className="text-xs border border-[#E4E1D8] px-2 py-1 rounded-md text-[#0B2545] hover:bg-[#F1EFE9]"
              >
                {f.label}
              </button>
            ))}
            <button onClick={onClose}><X className="h-5 w-5 text-[#5B5B62]" /></button>
          </div>
        </div>
        <div className="overflow-y-auto p-6 prose prose-sm max-w-none text-[#1F2937]">
          <ReactMarkdown>{doc.content_md}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

// ROI Calculator — mode Institution (employabilité)
function RoiCalculator({ onClose }) {
  const [users, setUsers] = useState(1000);
  const [insertionRate, setInsertionRate] = useState(15); // % uplift
  const [avgSalary, setAvgSalary] = useState(45000);
  const [engagementYears, setEngagementYears] = useState(3);
  const [platformCost, setPlatformCost] = useState(15); // €/user/year

  const additionalInsertions = Math.round(users * (insertionRate / 100));
  const socialROI = additionalInsertions * avgSalary * engagementYears;
  const totalCost = users * platformCost * engagementYears;
  const roi = totalCost > 0 ? (socialROI / totalCost).toFixed(1) : "∞";
  const netValue = socialROI - totalCost;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="roi-calculator-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#5B5B62]">Simulateur</div>
            <h3 className="text-2xl font-display text-[#0B2545]">ROI Calculator</h3>
            <p className="text-sm text-[#5B5B62] mt-1">Mode institution — impact employabilité</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-[#5B5B62]" /></button>
        </div>

        <div className="space-y-4">
          <Slider label="Nombre d'utilisateurs accompagnés" value={users} setValue={setUsers} min={100} max={100000} step={100} suffix="pers." testId="roi-users" />
          <Slider label="Amélioration du taux d'insertion (pts)" value={insertionRate} setValue={setInsertionRate} min={0} max={40} step={1} suffix="%" testId="roi-insertion" />
          <Slider label="Salaire moyen premier poste (€/an)" value={avgSalary} setValue={setAvgSalary} min={15000} max={120000} step={1000} suffix="€" testId="roi-salary" />
          <Slider label="Durée d'engagement moyen (années)" value={engagementYears} setValue={setEngagementYears} min={1} max={10} step={1} suffix="ans" testId="roi-years" />
          <Slider label="Coût ALTIORA par utilisateur (€/an)" value={platformCost} setValue={setPlatformCost} min={5} max={100} step={1} suffix="€" testId="roi-cost" />
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Insertions additionnelles" value={additionalInsertions.toLocaleString()} suffix="pers." testId="roi-out-insertions" />
          <MetricCard label="Valeur sociale générée" value={(socialROI / 1e6).toFixed(1)} suffix="M€" testId="roi-out-social" tone="success" />
          <MetricCard label="Coût plateforme total" value={(totalCost / 1e3).toFixed(0)} suffix="k€" testId="roi-out-cost" />
          <MetricCard label="ROI multiplicateur" value={`${roi}×`} suffix="" testId="roi-out-multiplier" tone="gold" />
        </div>

        <div className="mt-4 p-4 border border-[#E7D9B3] bg-[#FBF6EB] rounded-md">
          <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#7A5B14]">Valeur nette générée</div>
          <div className="text-3xl font-display text-[#7A5B14] mt-1" data-testid="roi-out-net">
            {(netValue / 1e6).toFixed(2)} M€
          </div>
          <div className="text-xs text-[#7A5B14] mt-1">
            Sur {engagementYears} ans, hors gains indirects (rétention, contribution fiscale, dividendes sociaux).
          </div>
        </div>

        <div className="mt-4 text-[10px] font-mono text-[#8B8B8F]">
          Modèle indicatif. Les chiffres réels dépendent de votre contexte. Contactez partenaires@altioracoop.com pour une étude ROI personnalisée.
        </div>
      </div>
    </div>
  );
}

function Slider({ label, value, setValue, min, max, step, suffix, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#5B5B62]">{label}</div>
        <div className="text-sm font-medium text-[#0B2545]">{value.toLocaleString()} {suffix}</div>
      </div>
      <input
        data-testid={testId}
        type="range" min={min} max={max} step={step}
        value={value} onChange={(e) => setValue(Number(e.target.value))}
        className="w-full accent-[#0B2545]"
      />
    </div>
  );
}

function MetricCard({ label, value, suffix, tone = "neutral", testId }) {
  const cls = tone === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : tone === "gold" ? "border-[#E7D9B3] bg-[#FBF6EB] text-[#7A5B14]"
    : "border-[#E4E1D8] bg-white text-[#0B2545]";
  return (
    <div className={`border ${cls} rounded-md p-3`} data-testid={testId}>
      <div className="text-[9px] font-mono uppercase tracking-[0.14em] opacity-70">{label}</div>
      <div className="mt-1 text-xl font-display">{value} <span className="text-xs font-mono opacity-60">{suffix}</span></div>
    </div>
  );
}
